import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { ChatMessage } from '../types/foreman';
import type { EventFrame, ChatEventPayload, ChatMessageContent, ContentPart, UserRole } from '../types/gateway';
import { parseForemanResponse } from '../gateway/parser';
import { buildChatSend, fileToAttachment } from '../gateway/protocol';
import { gateway } from '../gateway/connection';
import { requestCountersign } from '../gateway/attributionApi';
import { normalizeSessionKey, matchesActiveSession } from '../gateway/sessionKeyFilter';
import type { SenderMeta } from '../gateway/userMessageMeta';
import { isExtractableDocument, extractDocumentText } from '../extractors';
import { buildAgentRoleContext } from '../config/roles';

interface UseMessagesProps {
  activeSessionId: string | null;
  teachMode?: boolean;
  chatHistory?: ChatMessage[];
  loadingHistory?: boolean;
  /** Active UI role — sent to the agent on every chat.send so it can adapt. */
  role?: UserRole;
  /** Logged-in user identity (from the auth JWT). When set, every outgoing
   *  message carries a hidden [SENDER] block so shared-thread history can be
   *  attributed to its author. */
  senderIdentity?: { username: string; name: string } | null;
}

/** Extract plain text from an OpenClaw message content */
function extractText(msg: ChatMessageContent | undefined): string {
  if (!msg) return '';
  if (typeof msg.content === 'string') return msg.content;
  if (Array.isArray(msg.content)) {
    return msg.content
      .filter((p: ContentPart) => p.type === 'text')
      .map((p) => (p as { text: string }).text)
      .join('');
  }
  return '';
}

export function useMessages({ activeSessionId, teachMode = false, chatHistory = [], loadingHistory = false, role, senderIdentity }: UseMessagesProps) {
  const [liveMessages, setLiveMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const streamRef = useRef<string>('');
  const runIdRef = useRef<string | null>(null);
  const prevSessionRef = useRef<string | null>(null);
  // True from the instant a send starts (live bubble added) until isStreaming
  // takes over as the dedup gate. Closes the async window opened by the
  // `await requestCountersign` between adding the bubble (setLiveMessages below)
  // and setIsStreaming(true) — during which a Bug-C history refresh from a
  // PREVIOUS turn could otherwise prune the just-added, not-yet-persisted bubble.
  const sendingRef = useRef(false);

  // Clear live messages when session changes. We keep messages already tagged
  // for the INCOMING session — the compose-guard's "Start my own thread" path
  // echoes the carried-over draft into the new session key in the same batch
  // as the switch, and a blanket clear would wipe it before it ever rendered.
  useEffect(() => {
    if (prevSessionRef.current !== activeSessionId) {
      prevSessionRef.current = activeSessionId;
      setLiveMessages((prev) => prev.filter((m) => m.sessionId === activeSessionId));
      streamRef.current = '';
      setIsStreaming(false);
      sendingRef.current = false;
      runIdRef.current = null;
    }
  }, [activeSessionId]);

  // Merge: history (from useSessions) + live messages (from this session's
  // events). Memoized so it's a STABLE reference across unrelated re-renders
  // (email poll, ACL refresh) — ChatArea's auto-scroll keys off this array and
  // must not fire on every render.
  const messages = useMemo(
    () => [...chatHistory, ...liveMessages],
    [chatHistory, liveMessages]
  );

  // BUG C dedup — when useSessions refreshes chatHistory after a completed turn,
  // the just-finished turn now lives in BOTH chatHistory and liveMessages, so
  // the merge above would double-render it. Prune live messages already
  // represented in the refreshed history.
  //
  // Identity: OpenClaw's chat.history rows carry NO stable per-message id (only
  // role/content/timestamp — see OCHistoryMessage), and the live `runId` is a
  // per-run correlation token that is never persisted back into history. So
  // there is no shared id to match on; we dedup by CONTENT, made safe three ways:
  //  (a) match against the live message's `matchContent` (the history-equivalent
  //      content) when present, so a document-extracted user message — whose live
  //      bubble shows the raw caption but whose history row carries the appended
  //      `[Extracted from …]` block — still matches and gets pruned;
  //  (b) ONE-TO-ONE: consume each history row at most once (multiset match), so a
  //      genuine duplicate ("ok" typed twice) can't both prune against a single
  //      history "ok" — the second copy survives until history catches up;
  //  (c) skip the prune entirely while a send is in flight (sendingRef) or a turn
  //      is streaming (isStreaming) — never yank a bubble that history hasn't had
  //      a chance to record yet.
  useEffect(() => {
    if (chatHistory.length === 0 || isStreaming || sendingRef.current) return;
    setLiveMessages((prev) => {
      // Multiset of available history rows, keyed by type + normalized content.
      const avail = new Map<string, number>();
      for (const h of chatHistory) {
        const key = h.type + '\n' + h.content.trim();
        avail.set(key, (avail.get(key) || 0) + 1);
      }
      let changed = false;
      const next = prev.filter((lm) => {
        // An undelivered send is never in history — keep it, don't prune it
        // against an identical older row.
        if (lm.sendFailed) return true;
        const key = lm.type + '\n' + (lm.matchContent ?? lm.content).trim();
        const count = avail.get(key) || 0;
        if (count > 0) {
          avail.set(key, count - 1); // consume this history row — one-to-one
          changed = true;
          return false; // prune: represented in history
        }
        return true; // keep: no unconsumed history row matches
      });
      return changed ? next : prev;
    });
  }, [chatHistory, isStreaming]);

  useEffect(() => {
    // Listen for chat events from the gateway
    const unsub = gateway.onEvent((evt: EventFrame) => {
      if (evt.event !== 'chat') return;

      const payload = evt.payload as ChatEventPayload;
      if (!payload) return;

      const { state, message, runId } = payload;
      // Normalize "agent:main:KEY" → the short key. This gate is the ONLY thing
      // keeping a broadcast reply for another thread out of the active view —
      // pinned + tested in gateway/sessionKeyFilter.ts.
      const sessionKey = normalizeSessionKey(payload.sessionKey);

      // Only handle events for the active session
      if (!matchesActiveSession(payload.sessionKey, activeSessionId)) return;

      const msgId = runId || self.crypto?.randomUUID?.() || (Math.random().toString(36) + Date.now().toString(36));

      if (state === 'delta') {
        setIsStreaming(true);
        runIdRef.current = runId || null;
        const text = extractText(message);
        if (typeof text === 'string' && text.length > 0) {
          // OpenClaw sends accumulated text, not incremental deltas
          if (text.length >= (streamRef.current?.length || 0)) {
            streamRef.current = text;
          }
        }

        const parsed = parseForemanResponse(streamRef.current);
        setLiveMessages((prev) => {
          const existing = prev.find((m) => m.id === msgId);
          if (existing) {
            return prev.map((m) =>
              m.id === msgId ? { ...m, content: streamRef.current, parsed } : m
            );
          }
          return [
            ...prev,
            {
              id: msgId,
              type: 'foreman',
              content: streamRef.current,
              parsed,
              sender: 'Foreman',
              timestamp: new Date().toISOString(),
              sessionId: sessionKey,
            },
          ];
        });
      } else if (state === 'final') {
        setIsStreaming(false);
        const text = extractText(message) || streamRef.current;
        streamRef.current = '';
        runIdRef.current = null;

        if (text.trim()) {
          const parsed = parseForemanResponse(text);
          setLiveMessages((prev) => {
            const existing = prev.find((m) => m.id === msgId);
            if (existing) {
              return prev.map((m) =>
                m.id === msgId ? { ...m, content: text, parsed } : m
              );
            }
            return [
              ...prev,
              {
                id: msgId,
                type: 'foreman',
                content: text,
                parsed,
                sender: 'Foreman',
                timestamp: new Date().toISOString(),
                sessionId: sessionKey,
              },
            ];
          });
        }
      } else if (state === 'aborted') {
        setIsStreaming(false);
        streamRef.current = '';
        runIdRef.current = null;
      }
    });

    return unsub;
  }, [activeSessionId]);

  const sendMessage = useCallback(
    async (content: string, files?: File[], opts?: { sessionKeyOverride?: string }) => {
      if (!content.trim() && (!files || files.length === 0)) return;

      // Mark send in flight NOW — before the live bubble is added — so the
      // Bug-C dedup can't prune this not-yet-persisted message during the
      // `await requestCountersign` window before setIsStreaming(true) below.
      sendingRef.current = true;

      // sessionKeyOverride lets the compose-guard send a carried-over draft
      // into a just-created thread before the activeSessionId state has
      // propagated through React.
      const sessionKey = opts?.sessionKeyOverride || activeSessionId || 'main';

      let gatewayContent = teachMode ? `[TEACH] ${content}` : content;

      // Add user message to local state. senderVerified:true — it's literally
      // this device's own send, no countersign round-trip needed to trust it.
      const userMsg: ChatMessage = {
        id: self.crypto?.randomUUID?.() || (Math.random().toString(36) + Date.now().toString(36)),
        type: 'user',
        content,
        sender: 'You',
        timestamp: new Date().toISOString(),
        sessionId: sessionKey,
        senderInfo: senderIdentity
          ? { username: senderIdentity.username, name: senderIdentity.name, stamp: null }
          : undefined,
        senderVerified: senderIdentity ? true : undefined,
      };
      setLiveMessages((prev) => [...prev, userMsg]);

      // Separate files: images go as attachments, documents get text extracted
      const imageFiles: File[] = [];
      const docFiles: File[] = [];
      if (files) {
        for (const f of files) {
          if (isExtractableDocument(f)) {
            docFiles.push(f);
          } else {
            imageFiles.push(f);
          }
        }
      }

      // ALL the pre-send async work (doc extract, attachment encode, countersign)
      // AND the send run under ONE try/finally so the send gate can NEVER get
      // stuck true — a throw in extractDocumentText / fileToAttachment (a bad
      // file) / requestCountersign, before the happy-path reset below, would
      // otherwise leave sendingRef=true forever and permanently disable the
      // Bug-C dedup for this thread (every later turn would double-render).
      try {
        // Extract text from documents client-side
        const visionFiles: File[] = [];
        if (docFiles.length > 0) {
          const results = await Promise.all(docFiles.map(extractDocumentText));
          const blocks: string[] = [];
          for (const result of results) {
            if (result.ok) {
              const ext = result.fileName.split('.').pop()?.toUpperCase() || 'FILE';
              blocks.push(`[Extracted from ${result.fileName} (${ext})]:\n\n${result.text}`);
            } else if (result.visionFiles?.length) {
              visionFiles.push(...result.visionFiles);
              blocks.push(
                `[${result.fileName} has no embedded text. ${result.visionFiles.length} rendered page image(s) are attached for AI Vision OCR. Treat those image attachments as the source document.]`
              );
            } else {
              blocks.push(`[Failed to extract ${result.fileName}: ${result.error}. Please paste the text directly or send a photo/screenshot.]`);
            }
          }
          if (blocks.length > 0) {
            gatewayContent = gatewayContent
              ? gatewayContent + '\n\n' + blocks.join('\n\n')
              : blocks.join('\n\n');
            // Stamp the live bubble with its history-equivalent content so the
            // Bug-C dedup can match+prune it once history refreshes. History
            // strips the hidden [TEACH]/[SENDER]/[ROLE_CONTEXT] blocks but KEEPS
            // the `[Extracted from …]` block, so the match key is the raw caption
            // plus the extracted blocks (NOT the [TEACH]-prefixed gatewayContent).
            const historyEquivalent = content
              ? content + '\n\n' + blocks.join('\n\n')
              : blocks.join('\n\n');
            setLiveMessages((prev) =>
              prev.map((m) => (m.id === userMsg.id ? { ...m, matchContent: historyEquivalent } : m))
            );
          }
        }

        // Build attachments from image files only (OpenClaw format)
        const attachmentFiles = [...imageFiles, ...visionFiles];
        const attachments = attachmentFiles.length > 0
          ? await Promise.all(attachmentFiles.map(fileToAttachment))
          : undefined;

        // Countersign: ask the auth server to stamp this send so teammates can
        // later verify the [SENDER] attribution. Hard-capped time budget inside
        // requestCountersign; on ANY failure we proceed with stamp:null — a
        // message is never blocked or delayed on attribution plumbing.
        let sender: SenderMeta | undefined;
        if (senderIdentity) {
          const stamp = await requestCountersign(sessionKey);
          sender = { username: senderIdentity.username, name: senderIdentity.name, stamp };
        }

        // Send via gateway request — attach role context so the agent adapts
        // its behavior to the user's selected role.
        const roleContext = role ? buildAgentRoleContext(role) : undefined;
        const params = buildChatSend(sessionKey, gatewayContent, attachments, roleContext, sender);

        // Reset stream state for incoming response. isStreaming now guards the
        // dedup, so the sendingRef window can close before the send.
        streamRef.current = '';
        setIsStreaming(true);
        sendingRef.current = false;
        await gateway.request('chat.send', params);
      } catch (err) {
        console.error('[useMessages] send failed:', err);
        setIsStreaming(false);
        // Mark the bubble UNDELIVERED so the dedup can't prune it against an
        // identical OLDER history row — this message never reached the server,
        // so it isn't (and won't be) in history.
        setLiveMessages((prev) =>
          prev.map((m) => (m.id === userMsg.id ? { ...m, sendFailed: true } : m))
        );
      } finally {
        // Belt-and-suspenders: never leave the send gate stuck true.
        sendingRef.current = false;
      }
    },
    [activeSessionId, teachMode, role, senderIdentity]
  );

  const clearMessages = useCallback(() => {
    setLiveMessages([]);
    streamRef.current = '';
    setIsStreaming(false);
    sendingRef.current = false;
  }, []);

  return {
    messages,
    isStreaming: isStreaming || loadingHistory,
    sendMessage,
    clearMessages,
  };
}
